import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import './AllRecommendationRulesPage.css';
import './FeaturedTab.css';
import useCatalogManagementStore from '@/stores/AdminStore/CatalogManagementStore';
import { MessageModal } from '@/Components/common/MessageModal';
import ConfirmationModal from '@/Components/common/ConfirmationModal/ConfirmationModal';
import LoadingSpinner from '@/Components/common/LoadingSpinner';
import RecommendationRuleModal from './RecommendationRuleModal';

const AllRecommendationRulesPage: React.FC = () => {
  const navigate = useNavigate();
  const { recommendationRules, deleteRecommendationRule, fetchRecommendationRules } = useCatalogManagementStore();

  const [isOperationLoading, setIsOperationLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;
  const [ruleModalOpen, setRuleModalOpen] = useState(false);
  const [editingRuleId, setEditingRuleId] = useState<number | null>(null);
  const [editingRuleData, setEditingRuleData] = useState<{
    rule_name: string;
    rule_condition: string;
    courses: Array<{ id: string; title: string }>;
  } | null>(null);

  const [messageModal, setMessageModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'success' | 'error';
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'success',
  });

  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    isOpen: boolean;
    ruleId: string | null;
    ruleName: string;
  }>({
    isOpen: false,
    ruleId: null,
    ruleName: '',
  });

  const showMessageModal = (title: string, message: string, type: 'success' | 'error') => {
    setMessageModal({ isOpen: true, title, message, type });
  };

  const closeMessageModal = () => {
    setMessageModal({ ...messageModal, isOpen: false });
  };

  const openDeleteConfirmation = (ruleId: string, ruleName: string) => {
    setDeleteConfirmation({
      isOpen: true,
      ruleId,
      ruleName,
    });
  };

  const closeDeleteConfirmation = () => {
    setDeleteConfirmation({
      isOpen: false,
      ruleId: null,
      ruleName: '',
    });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmation.ruleId) return;

    setIsOperationLoading(true);
    try {
      await deleteRecommendationRule(parseInt(deleteConfirmation.ruleId));
      closeDeleteConfirmation();
      showMessageModal('Success', 'Recommendation rule deleted successfully!', 'success');
    } catch (error: any) {
      console.error('Failed to delete recommendation rule:', error);
      closeDeleteConfirmation();
      showMessageModal('Error', error.message || 'Failed to delete recommendation rule. Please try again.', 'error');
    } finally {
      setIsOperationLoading(false);
    }
  };

  const openRuleModal = () => {
    setEditingRuleId(null);
    setEditingRuleData(null);
    setRuleModalOpen(true);
  };

  const openEditRuleModal = (ruleId: string) => {
    const rule = recommendationRules?.find(r => r.id === ruleId);
    if (!rule) return;

    setEditingRuleId(parseInt(ruleId));
    setEditingRuleData({
      rule_name: rule.rule_name,
      rule_condition: rule.rule_condition,
      courses: rule.courses,
    });
    setRuleModalOpen(true);
  };

  const closeRuleModal = () => {
    setRuleModalOpen(false);
    setEditingRuleId(null);
    setEditingRuleData(null);
  };

  const handleRuleModalSuccess = (message: string) => {
    showMessageModal('Success', message, 'success');
  };

  const handleRuleModalError = (message: string) => {
    showMessageModal('Error', message, 'error');
  };

  // Filter rules based on search term
  const filteredRules = recommendationRules?.filter((rule) => {
    const nameMatch = rule.rule_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const conditionMatch = rule.rule_condition?.toLowerCase().includes(searchTerm.toLowerCase());
    const courseMatch = rule.courses?.some((course) =>
      course.title?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    return nameMatch || conditionMatch || courseMatch;
  }) || [];

  // Pagination logic
  const totalRules = filteredRules.length;
  const totalPages = Math.ceil(totalRules / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedRules = filteredRules.slice(startIndex, endIndex);

  // Reset to page 1 when search term changes
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="all-recommendation-rules-page">
      <div className="all-recommendation-rules-header">
        <button
          className="all-recommendation-rules-back-button"
          onClick={() => navigate('/admin/content/catalog', { state: { activeTab: 'featured' } })}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Back
        </button>
        <div className="all-recommendation-rules-title-wrapper">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="all-recommendation-rules-icon">
            <path
              d="M22 12h-4l-3 9L9 3l-3 9H2"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <h1 className="all-recommendation-rules-title">All Recommendation Rules</h1>
        </div>
        <div className="all-recommendation-rules-count">
          {recommendationRules?.length || 0} {recommendationRules?.length === 1 ? 'Rule' : 'Rules'}
        </div>
        <button className="admin-catalog-add-rule-button" onClick={openRuleModal}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          Add Rule
        </button>
      </div>

      {/* Search Bar */}
      {recommendationRules && recommendationRules.length > 0 && (
        <div className="all-recommendation-rules-search-wrapper">
          <div className="all-recommendation-rules-search-container">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="all-recommendation-rules-search-icon">
              <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
              <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <input
              type="text"
              className="all-recommendation-rules-search-input"
              placeholder="Search by rule name, condition, or course..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
            {searchTerm && (
              <button
                className="all-recommendation-rules-search-clear"
                onClick={() => {
                  setSearchTerm('');
                  setCurrentPage(1);
                }}
                aria-label="Clear search"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            )}
          </div>
        </div>
      )}

      {!recommendationRules || recommendationRules.length === 0 ? (
        <div className="all-recommendation-rules-empty-state">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path
              d="M22 12h-4l-3 9L9 3l-3 9H2"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <h3>No Recommendation Rules Yet</h3>
          <p>Create smart recommendation rules to automatically suggest relevant courses to learners based on their role, department, tenure, or completion history.</p>
        </div>
      ) : filteredRules.length === 0 ? (
        <div className="all-recommendation-rules-empty-state">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path
              d="M22 12h-4l-3 9L9 3l-3 9H2"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <h3>No Rules Found</h3>
          <p>No recommendation rules match your search criteria. Try adjusting your search.</p>
        </div>
      ) : (
        <>
          <div className="all-recommendation-rules-list">
            {paginatedRules.map((rule) => (
            <div key={rule.id} className="all-recommendation-rule-item">
              <div className="all-recommendation-rule-header">
                <div className="all-recommendation-rule-title-row">
                  <h3 className="all-recommendation-rule-name">{rule.rule_name}</h3>
                  <span className={`all-recommendation-status-badge all-recommendation-${rule.status?.toLowerCase() || 'inactive'}`}>
                    {rule.status || 'Inactive'}
                  </span>
                </div>
                <div className="all-recommendation-rule-actions">
                  <button
                    className="all-recommendation-action-button"
                    aria-label="Edit"
                    onClick={() => openEditRuleModal(rule.id)}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  <button
                    className="all-recommendation-action-button"
                    aria-label="Delete"
                    onClick={() => openDeleteConfirmation(rule.id, rule.rule_name)}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              </div>
              <p className="all-recommendation-rule-description">{rule.rule_condition}</p>
              <div className="all-recommendation-rule-tags">
                {rule.courses.map((course, index) => (
                  <span key={index} className="all-recommendation-rule-tag">
                    {course.title}
                  </span>
                ))}
              </div>
            </div>
          ))}
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="all-recommendation-rules-pagination">
              <button
                className="all-recommendation-rules-pagination-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Previous
              </button>

              <div className="all-recommendation-rules-pagination-pages">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                  // Show first page, last page, current page, and pages around current
                  if (
                    page === 1 ||
                    page === totalPages ||
                    (page >= currentPage - 1 && page <= currentPage + 1)
                  ) {
                    return (
                      <button
                        key={page}
                        className={`all-recommendation-rules-pagination-page ${page === currentPage ? 'active' : ''}`}
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </button>
                    );
                  } else if (page === currentPage - 2 || page === currentPage + 2) {
                    return <span key={page} className="all-recommendation-rules-pagination-ellipsis">...</span>;
                  }
                  return null;
                })}
              </div>

              <button
                className="all-recommendation-rules-pagination-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          )}

          {/* Results Info */}
          <div className="all-recommendation-rules-results-info">
            Showing {startIndex + 1} - {Math.min(endIndex, totalRules)} of {totalRules} {totalRules === 1 ? 'rule' : 'rules'}
          </div>
        </>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmation.isOpen && ReactDOM.createPortal(
        <ConfirmationModal
          isOpen={deleteConfirmation.isOpen}
          onClose={closeDeleteConfirmation}
          onConfirm={handleDeleteConfirm}
          title="Delete Recommendation Rule"
          message={`Are you sure you want to delete "${deleteConfirmation.ruleName}"? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          confirmButtonStyle="danger"
          isLoading={isOperationLoading}
        />,
        document.body
      )}

      {/* Message Modal for Success/Error */}
      {messageModal.isOpen && ReactDOM.createPortal(
        <MessageModal
          isOpen={messageModal.isOpen}
          onClose={closeMessageModal}
          type={messageModal.type}
          title={messageModal.title}
          message={messageModal.message}
        />,
        document.body
      )}

      {/* Full Screen Loading Spinner */}
      {isOperationLoading && ReactDOM.createPortal(
        <LoadingSpinner fullScreen message="Processing..." />,
        document.body
      )}

      {/* Recommendation Rule Modal */}
      <RecommendationRuleModal
        isOpen={ruleModalOpen}
        onClose={closeRuleModal}
        onSuccess={handleRuleModalSuccess}
        onError={handleRuleModalError}
        editRuleId={editingRuleId}
        editRuleData={editingRuleData}
      />
    </div>
  );
};

export default AllRecommendationRulesPage;
